<template>
  <div class="field">
    <label class="field-label" v-if="label">{{ label }}</label>
    <template v-if="filesTypeNote">
      <slot :filesTypeNote="filesTypeNote" />
    </template>
    <slot />
    <div id="status-bar" />
    <div class="upload-button-container">
      <div
        v-for="pair in buttonLabelPairs"
        :id="pair.id"
        :key="pair.id"
        class="uppy-file-input"
      />
    </div>
    <div v-if="fileList.length > 0">
      {{ fileList.length }} File(s) Awaiting Upload:
      <ul>
        <li
          v-for="file in fileList"
          :key="file.id"
          class="file-list-item"
        >
          <span>
            {{ file.name }}
          </span>
          <button class="remove-button" @click="removeFileFromUppy(file.id)">
            X
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { presignUrl, confirmFileAdded } from '@/services/HttpSvc'
import NotifySvc from '@/services/NotifySvc'
import Uppy from '@uppy/core'
import StatusBar from '@uppy/status-bar'
import FileInput from '@uppy/file-input'
import AwsS3 from '@uppy/aws-s3'
import '@uppy/core/dist/style.css'
import '@uppy/file-input/dist/style.css'
import '@uppy/status-bar/dist/style.css'
const ONE_MB = 1000000
const maxTotalFileSize = 100 * ONE_MB // 100MB
export default {

  name: 'DSFileUpload',

  props: {
    label: {
      type: String,
      default: '',
    },
    description: {
      type: String,
      default: '',
    },
    additionalInfo: {
      type: String,
      default: '',
    },
    buttonLabels: {
      type: Array,
      default: () => [],
    },
    previouslyUploadedFiles: {
      type: Array,
      default: () => [],
    },
    allowedFileTypes: {
      type: Array,
      default: () => [],
    },
    isLogo: {
      type: Boolean,
      default: false,
    },
    maxFiles: {
      type: Number,
      default: 5,
    },
  },

  data() {
    return {
      uppy: Uppy({
          id: 'imageUploads',
          debug: process.env.NODE_ENV !== 'production',
          onBeforeFileAdded: this.onBeforeFileAdded,
        restrictions: {
          maxFileSize: maxTotalFileSize, // 100MB
          maxNumberOfFiles: this.maxFiles,
          allowedFileTypes: this.allowedFileTypes,
        },

          // onBeforeUpload: (files, done) => Promise.resolve(),
      }),
      fileList: [],
    }
  },

  computed: {
    filesTypeNote() {
      return 'Supported filetypes: ' + this.allowedFileTypes.join(', ')
    },

    buttonLabelPairs(){
      return this.buttonLabels.map(l => ({
        label: l,
        id: l.replace(/^[^a-z]+|[^\w:.-]+/gi, ""),
      }));
    },

    previouslyUploadedFileNames(){
      this.uppy.setOptions({
        restrictions: {
          maxFileSize: maxTotalFileSize,
          maxNumberOfFiles: this.maxFiles - this.previouslyUploadedFiles.length,
          allowedFileTypes: this.allowedFileTypes,
        },
      })

      return this.previouslyUploadedFiles.map(f => f.name || f.filename);
    },
  },


  mounted() {
    this.buttonLabelPairs.forEach(pair => {
      this.uppy.use(FileInput, {
          target: '#' + pair.id,
          id: pair.id,
        locale: {
          strings: {
            chooseFiles: `Upload ${pair.label}`,
          },
        },
      });
    });

    this.uppy.use(StatusBar, {
      target: '#status-bar',
      hideUploadButton: true,
    })

    this.uppy.on('complete',
      result => this.onUploadComplete(result))

    this.uppy.on('file-added', file => {
      this.fileList.push({
        name: file.data.name,
        id: file.id,
      })
    },
  )

    this.uppy.on('file-removed', file => this.fileList = this.fileList.filter(f => f.id !== file.id));

    this.uppy.on('restriction-failed', (file, error) => {
      if(error.message !== 'Cannot add the file because onBeforeFileAdded returned false.'){
        NotifySvc.error({
          message: error.message,
          duration: 5000,
        });
      }
    })
  },

  methods: {

    createAwsS3Config(nominationId) {
      //console.log('createAwsS3Config' + nominationId)
      if (this.__hasLoadedAwsPlugin()) return
      const isLogo = this.isLogo
      this.uppy.use(AwsS3, {
        async getUploadParameters(file) {
          //console.log('uppy.use' + nominationId)
          const presign = await presignUrl(file.name, nominationId, isLogo)
          // TODO: move this confirmation to be after Uppy reports success
          await confirmFileAdded(presign.id, file.name)
          return {
            method: presign.method,
            url: presign.url,
            fields: presign.fields,
          }
        },
      })
    },

    async uploadFiles(nominationId) {
      //console.log('uploadFiles' + nominationId)
      this.createAwsS3Config(nominationId)
      return this.uppy.upload()
    },

    removeFileFromUppy(fileId) {
      this.uppy.removeFile(fileId)
    },

    onUploadComplete(result) {
      if (result.failed.length) {
        NotifySvc.error(`${result.failed.length} file upload${result.failed.length === 1 ? '' : 's'} failed.`);
      } else if (result.successful.length) {
        NotifySvc.success(`File${result.successful.length === 1 ? '' : 's'} uploaded successfully!`);
      }
      //console.debug('Upload complete! We’ve uploaded these files:', result)
    },

    /**
     * Checks for the existance of an AwsS3 plugin on this.uppy instance.
     * We can't load more than one AWS plugin.
     * @return {Boolean} True if this.uppy already has the AWS plugin loaded.
     */
    __hasLoadedAwsPlugin() {
      return this.uppy.plugins.uploader && this.uppy.plugins.uploader.find(p => p.id === 'AwsS3')
    },
    onBeforeFileAdded(currentFile, files) {
      let TotalFileSize = 0;

      for (const key in files) {
        TotalFileSize = TotalFileSize + files[key].size;
      }

      const splitFileName = currentFile.name.split('.');
      const fileType = "." + splitFileName[splitFileName.length - 1].toLowerCase();
      if(!this.allowedFileTypes.includes(fileType)) {
        NotifySvc.error({
          message: `This file type is not supported.  Please try another file.`,
          duration: 5000,
        });
        return false
      }

      const grandTotalFileSize = currentFile.data.size + TotalFileSize;

      if (grandTotalFileSize >= maxTotalFileSize) {
        NotifySvc.error({
          message: `Total File Size (${grandTotalFileSize/ONE_MB}MB) is too large. Please reduce the size of your files before continuing.`,
          duration: 5000,
        });
        return false
      }

      if(this.previouslyUploadedFileNames.includes(currentFile.name)){
        NotifySvc.error({
          message: `You have already uploaded a file with that name. If you would like to upload a new version of this file, remove the old file first.`,
          duration: 5000,
        });
        return false
      }


      return true
    },
  },
}


</script>

<style lang="css" scoped>
  label.field-label {
    margin-bottom: 8px;
  }

  .field {
    width: 100%;
  }
  .upload-button-container {
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
  }

  .file-list-item {
    background: none;
  }

  .remove-button {
    color: #dc3996;
    background-color: rgba(255,255,255, 0);
    font-size: inherit;
    line-height: 1;
    vertical-align: unset;
  }

</style>

<style lang="css">
  .uppy-file-input button {
    color: #dc3996;
    border-color: #dc3996;
    transition: color 0.25s, background-color 0.25s;
  }

  .uppy-file-input button:hover{
    color: #fff;
    background-color: #dc3996;
  }
</style>
